// Noodl SDK - webpack handles this require properly
const Noodl = require('@noodl/noodl-sdk');
const React = require('react');
import { CustomSelectAlt } from '@shared/components/CustomSelectAlt/CustomSelectAlt';

export const CustomSelectAltNode = Noodl.defineReactNode({
  name: 'Prodikt Custom Select Alt',
  displayName: 'Custom Select Alt',
  category: 'Prodikt Components',
  
  getReactComponent() {
    return CustomSelectAlt;
  },

  inputProps: {
    // Basic properties
    label: {
      type: 'string',
      displayName: 'Label',
      group: 'General'
    },
    placeholder: {
      type: 'string',
      displayName: 'Placeholder',
      default: 'Select...',
      group: 'General'
    },
    disabled: {
      type: 'boolean',
      displayName: 'Disabled',
      default: false,
      group: 'General'
    },
    fullWidth: {
      type: 'boolean',
      displayName: 'Full Width',
      default: false,
      group: 'Layout'
    },
    size: {
      type: {
        name: 'enum',
        enums: [
          { label: 'Small', value: 'small' },
          { label: 'Medium', value: 'medium' },
          { label: 'Large', value: 'large' }
        ]
      },
      displayName: 'Size',
      default: 'medium',
      group: 'Layout'
    },

    // Options
    options: {
      type: 'array',
      displayName: 'Options',
      group: 'Data',
      default: []
    },

    // Value
    value: {
      type: '*',
      displayName: 'Value',
      group: 'Data'
    },

    // Features
    multiSelect: {
      type: 'boolean',
      displayName: 'Multi Select',
      default: false,
      group: 'Features'
    },
    searchable: {
      type: 'boolean',
      displayName: 'Searchable',
      default: false,
      group: 'Features'
    },
    searchPlaceholder: {
      type: 'string',
      displayName: 'Search Placeholder',
      default: 'Search...',
      group: 'Features'
    },
    noOptionsMessage: {
      type: 'string',
      displayName: 'No Options Message',
      default: 'No options found',
      group: 'Features'
    },
    removable: {
      type: 'boolean',
      displayName: 'Removable',
      default: false,
      group: 'Features'
    },
    creatable: {
      type: 'boolean',
      displayName: 'Creatable',
      default: false,
      group: 'Features'
    },
    sortOptions: {
      type: 'boolean',
      displayName: 'Sort Options',
      default: true,
      group: 'Features'
    },

    // Create options
    createLabel: {
      type: 'string',
      displayName: 'Create Label',
      default: 'Create new option',
      group: 'Create'
    },
    createPlaceholder: {
      type: 'string',
      displayName: 'Create Placeholder',
      default: 'Enter option name',
      group: 'Create'
    },
    createButtonText: {
      type: 'string',
      displayName: 'Create Button Text',
      default: 'Create',
      group: 'Create'
    },
    autoSelectCreated: {
      type: 'boolean',
      displayName: 'Auto Select Created',
      default: false,
      group: 'Create'
    },
    createMaxLength: {
      type: 'number',
      displayName: 'Create Max Length',
      group: 'Create'
    },

    // Messages
    error: {
      type: 'string',
      displayName: 'Error Message',
      group: 'Messages'
    },
    helperText: {
      type: 'string',
      displayName: 'Helper Text',
      group: 'Messages'
    },

    // Styling
    className: {
      type: 'string',
      displayName: 'CSS Class',
      group: 'Style'
    },
    borderColor: {
      type: 'string',
      displayName: 'Border Color',
      group: 'Style'
    }
  },

  outputProps: {
    onChange: {
      type: 'signal',
      displayName: 'Changed'
    },
    onRemove: {
      type: 'signal',
      displayName: 'Removed'
    },
    onCreate: {
      type: 'signal',
      displayName: 'Created'
    },
    currentValue: {
      type: '*',
      displayName: 'Current Value'
    },
    createdValue: {
      type: 'string',
      displayName: 'Created Value'
    },
    removedValue: {
      type: 'string',
      displayName: 'Removed Value'
    },
    autoSelectCreated: {
      type: 'boolean',
      displayName: 'Auto Select Created'
    }
  },

  changed: {
    value: function(value) {
      this._outputValue = value;
      if (this.outputPropValues) {
        this.outputPropValues.currentValue = value;
      }
      this.flagOutputDirty('currentValue');
    }
  },

  update: function() {
    // Sync output if props.value has changed (handles initial connected values)
    const currentValue = this.props.value;
    if (this._outputValue !== currentValue) {
      this._outputValue = currentValue;
      if (this.outputPropValues) {
        this.outputPropValues.currentValue = currentValue;
      }
      this.flagOutputDirty('currentValue');
    }
  },

  setup: function(context, graphModel) {
    // Handle style objects
    if (!context.propsInput) context.propsInput = {};
  },

  initialize: function() {
    // Initialize output prop values first
    if (!this.outputPropValues) this.outputPropValues = {};

    // Initialize output value
    const initialValue = this.props.value || (this.props.multiSelect ? [] : '');
    this._outputValue = initialValue;
    this._lastPropsValue = initialValue;
    this.outputPropValues.currentValue = this._outputValue;

    // Flag output as dirty so initial value is sent
    this.flagOutputDirty('currentValue');

    // Poll for value changes from connected inputs
    this._valueCheckInterval = setInterval(() => {
      const currentPropsValue = this.props.value;
      if (JSON.stringify(this._lastPropsValue) !== JSON.stringify(currentPropsValue)) {
        this._lastPropsValue = currentPropsValue;
        this._outputValue = currentPropsValue || (this.props.multiSelect ? [] : '');
        if (this.outputPropValues) {
          this.outputPropValues.currentValue = this._outputValue;
        }
        this.flagOutputDirty('currentValue');
      }
    }, 100);

    // Set up onChange handler
    this.props.onChange = (value) => {
      this._outputValue = value;
      if (this.outputPropValues) {
        this.outputPropValues.currentValue = value;
      }
      this.setInputValue('value', value);
      this.flagOutputDirty('currentValue');
      this.sendSignalOnOutput('onChange');
    };

    // Set up onRemove handler
    this.props.onRemove = (value) => {
      if (this.outputPropValues) {
        this.outputPropValues.removedValue = value;
      }
      this.flagOutputDirty('removedValue');
      this.sendSignalOnOutput('onRemove');
    };

    // Set up onCreate handler
    this.props.onCreate = (value, autoSelect) => {
      if (this.outputPropValues) {
        this.outputPropValues.createdValue = value;
        this.outputPropValues.autoSelectCreated = autoSelect || false;
      }
      this.flagOutputDirty('createdValue');
      this.flagOutputDirty('autoSelectCreated');
      this.sendSignalOnOutput('onCreate');
    };

    // Set up onOptionsChange handler
    this.props.onOptionsChange = (options) => {
      this.setInputValue('options', options);
    };
  },

  methods: {
    setValue: function(value) {
      this.setInputValue('value', value);
    },
    getValue: function() {
      return this.props.value;
    },
    setOptions: function(options) {
      this.setInputValue('options', options);
    },
    clearValue: function() {
      this.setInputValue('value', this.props.multiSelect ? [] : '');
    }
  },

  dispose: function() {
    // Clean up interval when node is disposed
    if (this._valueCheckInterval) {
      clearInterval(this._valueCheckInterval);
    }
  }
});
